import { Fragment, useMemo } from "react";
import { styled } from "styled-components";

import { SectionLabel, Spacer, Text } from "@vericus/cadmus-ui";

import { QuestionSetsPreview } from "@/features/task/components/sheet-preview/question-sets-preview";
import * as styles from "@/features/task/components/sheet-preview/sheet-preview.css";
import { TaskBlockPreview } from "@/features/task/components/sheet-preview/task-block-preview";
import { PreviewQuestionBlock } from "@/features/task/components/sheet-preview/types";
import { usePreviewFromQuestionBlocks } from "@/features/task/components/sheet-preview/utils";
import {
  AssessmentSettingsFragment,
  QuestionBlockFragment,
  QuestionType,
  RequirementsFragment,
  ResourceFragment,
  TaskFormat,
  TaskKind,
} from "@/generated/graphql";
import { isGroupAssessment } from "@/graphql/assessment-settings-selectors";
import { TaskEditorContentReadOnly } from "@/ui/task/editor";
import { RequirementView } from "@/ui/task/requirements";
import { fromAssessmentSettings } from "@/ui/task/requirements/fromAssessmentSettings";
import { ResourceListReadOnly } from "@/ui/task/resources";
import { formatTaskTitle } from "@/utils/task-title";

interface Props {
  assessmentName: string;
  assessmentSettings: AssessmentSettingsFragment;
  taskKind: TaskKind;
  taskFormat: TaskFormat;
  /** Assessment task body content (for classic format). */
  content?: string | null;
  /** Question blocks for multiformat */
  questionBlocks?: QuestionBlockFragment[];
  /** Attached assessment resources */
  resources: ResourceFragment[];
  /** Optionally show the hidden settings in the Requirements.  */
  showHidden?: boolean;
}

/**
 * Shared assessment preview component that can display both classic and multiformat
 * assessments without any navigation or edit controls. Perfect for modals.
 */
export function AssessmentPreview(props: Props) {
  const {
    content,
    questionBlocks = [],
    assessmentName,
    taskKind,
    showHidden,
    resources,
    assessmentSettings,
    taskFormat,
  } = props;

  const requirements = fromAssessmentSettings(assessmentSettings);
  const isGA = isGroupAssessment(assessmentSettings);

  const previewBlocks = usePreviewFromQuestionBlocks(questionBlocks);
  const isMultiformat = taskFormat === TaskFormat.Multiformat;

  if (isMultiformat && questionBlocks.length > 0) {
    return (
      <MultiformatPreviewContent
        blocks={previewBlocks}
        assessmentName={assessmentName}
        assessmentSettings={assessmentSettings || null}
        requirements={requirements}
        resources={resources}
        showHidden={showHidden}
      />
    );
  }

  return (
    <>
      <div className={styles.section} style={{ paddingTop: 48 }}>
        {isGA && (
          <Text kind="displayThree">
            {formatTaskTitle(assessmentName, taskKind)}
          </Text>
        )}
        {!isGA && <Text kind="displayTwo">{assessmentName}</Text>}
      </div>

      <Spacer spacing={48} />

      <div className={styles.section}>
        <RequirementView
          requirements={requirements}
          showHidden={showHidden}
          format={TaskFormat.Multiformat}
        />
      </div>
      <Spacer spacing={48} />
      <TaskLabel title={taskKind === TaskKind.Peer ? "Instructions" : "Task"} />
      <TaskEditorContentReadOnly content={content || null} />
      <Spacer spacing={48} />
      <div className={styles.section} style={{ paddingBottom: 63 }}>
        <ResourceListReadOnly resources={resources} />
      </div>
    </>
  );
}

const TaskLabel = styled(SectionLabel)`
  padding: 0 45px 27px 45px;
  max-width: 800px;
  box-sizing: border-box;
  margin-right: auto;
  margin-left: auto;
  margin-bottom: -63px;
`;

// Multiformat preview component that respects edit control settings
interface MultiformatPreviewContentProps {
  blocks: PreviewQuestionBlock[];
  assessmentName: string;
  assessmentSettings: AssessmentSettingsFragment | null;
  requirements: RequirementsFragment;
  resources: ResourceFragment[];

  showHidden?: boolean;
}

function MultiformatPreviewContent({
  blocks,
  assessmentName,
  requirements,
  resources,
  showHidden,
}: MultiformatPreviewContentProps) {
  const [overviewBlock, questionBlocks] = useMemo(() => {
    let first: PreviewQuestionBlock | undefined = undefined;
    const rest: PreviewQuestionBlock[] = [];
    for (const blk of blocks) {
      if (blk.question?.questionType === QuestionType.Overview) {
        first = blk;
      } else {
        rest.push(blk);
      }
    }
    return [first, rest];
  }, [blocks]);

  return (
    <>
      <div className={styles.section} style={{ paddingTop: 48 }}>
        <Text kind="displayTwo">{assessmentName}</Text>
      </div>
      <Spacer spacing={48} />
      <div className={styles.section}>
        <RequirementView
          requirements={requirements}
          showHidden={showHidden}
          format={TaskFormat.Multiformat}
        />
      </div>
      <Spacer spacing={48} />
      {overviewBlock?.question && (
        <>
          <div className={styles.section}>
            <SectionLabel title="Assessment Overview" />
          </div>
          <div className={styles.questions}>
            <TaskBlockPreview
              question={overviewBlock.question}
              questionTitle=""
              points={null}
              isChildQuestion={false}
              correctResponseMarking={false}
            />
          </div>
        </>
      )}
      <Spacer spacing={48} />
      <div className={styles.section}>
        <ResourceListReadOnly resources={resources} />
      </div>
      <div className={styles.questions}>
        {questionBlocks.map((block) => {
          if (block.question) {
            return (
              <Fragment key={block.id}>
                {!block.isChildQuestion && (
                  <div className={styles.section}>
                    <div className={styles.divider} />
                  </div>
                )}
                <TaskBlockPreview
                  question={block.question}
                  questionTitle={block.longLabel}
                  points={block.points}
                  isChildQuestion={block.isChildQuestion}
                  correctResponseMarking={false}
                />
              </Fragment>
            );
          }
          if (block.sets) {
            return (
              <Fragment key={block.id}>
                <div className={styles.section}>
                  <div className={styles.divider} />
                </div>
                <QuestionSetsPreview
                  sets={block.sets}
                  groupLabel={block.longLabel}
                  groupPoints={block.points}
                  correctResponseMarking={false}
                />
              </Fragment>
            );
          }
          return null;
        })}
      </div>
    </>
  );
}
